import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { searchComics, getHotComics } from '@/lib/comics';
import { categories } from '@/mocks/categories';
import ComicCard from '@/components/base/ComicCard';
import SearchBox from '@/components/feature/SearchBox';
import type { Comic } from '@/types/comic';

type SortKey = 'relevance' | 'update' | 'hot';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'relevance', label: '综合' },
  { key: 'update', label: '最近更新' },
  { key: 'hot', label: '人气最高' },
];

function sortComics(list: Comic[], key: SortKey): Comic[] {
  const copy = [...list];
  if (key === 'hot') return copy.sort((a, b) => b.popularity - a.popularity);
  if (key === 'update') return copy.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  return copy;
}

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get('q') ?? '').trim();
  const [sortKey, setSortKey] = useState<SortKey>('relevance');
  const [activeCat, setActiveCat] = useState('全部');
  const [inputKey, setInputKey] = useState(keyword);

  useEffect(() => {
    setInputKey(keyword);
    setSortKey('relevance');
    setActiveCat('全部');
  }, [keyword]);

  useSEO({
    title: keyword ? `“${keyword}” 搜索结果 - 韩漫漫画` : '搜索 - 韩漫漫画',
    description: keyword
      ? `在韩漫平台搜索“${keyword}”，为你匹配标题、作者与简介相关的韩漫漫画作品。`
      : '在韩漫平台搜索你想要的韩国漫画作品。',
    keywords: '韩漫搜索,找韩漫,韩国漫画,漫画推荐',
    path: '/search',
  });

  const results = useMemo(() => {
    const base = searchComics(keyword);
    const byCat = activeCat === '全部' ? base : base.filter((c) => c.categories.includes(activeCat));
    return sortComics(byCat, sortKey);
  }, [keyword, activeCat, sortKey]);

  const hot = getHotComics(4);
  const catList = ['全部', ...categories.map((c) => c.id)];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800 font-medium">搜索</span>
      </nav>

      <h1 className="mt-5 font-heading font-bold text-2xl md:text-3xl text-foreground-950">站内搜索</h1>
      <p className="mt-2 text-sm text-foreground-600">
        支持按漫画标题、作者与简介关键词检索。
        {keyword && (
          <>
            当前关键词：<span className="text-primary-600 font-medium">“{keyword}”</span>
          </>
        )}
      </p>

      <div className="mt-5">
        <SearchBox key={inputKey} initialValue={keyword} size="lg" className="w-full max-w-3xl" />
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="flex items-start gap-3 min-w-0">
            <span className="shrink-0 text-sm text-foreground-500 pt-1.5">题材筛选</span>
            <div className="flex flex-wrap gap-2">
              {catList.map((id) => {
                const name = id === '全部' ? '全部' : categories.find((c) => c.id === id)?.name ?? id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveCat(id)}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                      activeCat === id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-foreground-500">排序</span>
          <div className="flex gap-1 p-1 rounded-full bg-background-200/70">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  sortKey === opt.key
                    ? 'bg-background-50 text-primary-600 font-medium'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className="ml-auto text-sm text-foreground-500">
            共找到 <span className="font-heading font-bold text-primary-600">{results.length}</span> 条结果
          </span>
        </div>
      </div>

      {!keyword ? (
        <div className="mt-10 py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-line text-3xl"></i>
          </div>
          <h2 className="mt-5 font-heading font-bold text-xl text-foreground-950">输入关键词开始搜索</h2>
          <p className="mt-2 text-sm text-foreground-600">试试搜索「爱情」「九尾狐」或作者名字。</p>
        </div>
      ) : results.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {results.map((comic) => (
            <ComicCard key={comic.id} comic={comic} showAuthor />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center py-14 rounded-lg border border-dashed border-background-300 bg-background-100">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h2 className="mt-5 font-heading font-bold text-xl text-foreground-950">没有找到「{keyword}」相关作品</h2>
          <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者看看下面的热门推荐。</p>
          <Link
            to="/search"
            className="mt-5 inline-flex items-center gap-1 px-5 h-10 rounded-md bg-secondary-500 text-background-50 text-sm font-label font-medium hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            清空搜索
          </Link>

          <div className="mt-10 w-full">
            <h3 className="font-heading font-bold text-lg text-foreground-950 text-left mb-4 px-1">热门推荐</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hot.map((comic) => (
                <ComicCard key={comic.id} comic={comic} showAuthor />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}